/**
 * What is kept across sessions in localStorage: the high score, and whether the
 * sound is off.
 *
 * Every access is wrapped. `localStorage` is not merely absent in some contexts
 * — reading it *throws* when storage is blocked (Safari private browsing, a
 * sandboxed or third-party frame, a browser configured to refuse it). Neither of
 * these is worth taking the game down over, so failure means "not kept" and play
 * carries on.
 */

const KEY = 'pyramid-scheme.high-score';
const MUTED_KEY = 'pyramid-scheme.muted';

/** @returns {number} the stored high score, or 0 if there is none to be had. */
export function loadHighScore() {
  try {
    const value = Number.parseInt(window.localStorage.getItem(KEY) ?? '', 10);
    // Anything unparseable, negative or absent reads as no score yet.
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

/**
 * Store `score` if it beats what is there.
 *
 * @returns {{best: number, record: boolean}} the high score after the attempt,
 *          and whether this score set it. `record` is false for a tie: matching
 *          your best is not beating it.
 */
export function recordScore(score) {
  const previous = loadHighScore();
  const value = Math.max(0, Math.floor(score));
  if (value <= previous) return { best: previous, record: false };

  try {
    window.localStorage.setItem(KEY, String(value));
  } catch {
    // Not stored, but it is still the best score of this session.
  }
  return { best: value, record: true };
}

/**
 * Whether the sound was left off. Defaults to on — a game that comes up silent
 * looks broken, so only an explicit choice to mute is remembered.
 */
export function loadMuted() {
  try {
    return window.localStorage.getItem(MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Remember the sound setting. Best effort, like the score. */
export function saveMuted(muted) {
  try {
    window.localStorage.setItem(MUTED_KEY, muted ? '1' : '0');
  } catch {
    // Not kept, but it still applies for this session.
  }
}
